import type React from "react";

import { useTranslation } from "react-i18next";
import styled from "styled-components";

interface Props {
  readonly type: "local" | "remote";
  readonly onClose: () => void;
}

export const AudioWarningBanner: React.FC<Props> = ({ type, onClose }) => {
  const { t } = useTranslation();
  const isLocal = type === "local";

  const getInstructions = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isChrome = userAgent.includes("chrome") && !userAgent.includes("edg");
    const isFirefox = userAgent.includes("firefox");
    const isSafari = userAgent.includes("safari") && !userAgent.includes("chrome");
    const isMac = userAgent.includes("mac");

    if (isChrome) {
      return (
        <>
          <InstructionStep>• {t("videocall.audio_warning.chrome_step1")}</InstructionStep>
          <InstructionStep>• {t("videocall.audio_warning.chrome_step2")}</InstructionStep>
          <InstructionStep>• {t("videocall.audio_warning.chrome_step3")}</InstructionStep>
        </>
      );
    }
    if (isFirefox) {
      return (
        <>
          <InstructionStep>• {t("videocall.audio_warning.firefox_step1")}</InstructionStep>
          <InstructionStep>• {t("videocall.audio_warning.firefox_step2")}</InstructionStep>
        </>
      );
    }
    if (isSafari) {
      return (
        <>
          <InstructionStep>• {t("videocall.audio_warning.safari_step1")}</InstructionStep>
          <InstructionStep>• {t("videocall.audio_warning.safari_step2")}</InstructionStep>
        </>
      );
    }
    if (isMac) {
      return (
        <>
          <InstructionStep>• {t("videocall.audio_warning.mac_step1")}</InstructionStep>
          <InstructionStep>• {t("videocall.audio_warning.mac_step2")}</InstructionStep>
        </>
      );
    }
    return (
      <>
        <InstructionStep>• {t("videocall.audio_warning.generic_step1")}</InstructionStep>
        <InstructionStep>• {t("videocall.audio_warning.generic_step2")}</InstructionStep>
      </>
    );
  };

  return (
    <BannerContainer>
      <Icon>⚠️</Icon>
      <Content>
        <Title>{isLocal ? t("videocall.audio_warning.local_title") : t("videocall.audio_warning.remote_title")}</Title>
        <Message>
          {isLocal ? (
            <>
              {t("videocall.audio_warning.local_message")}
              <Instructions>{getInstructions()}</Instructions>
            </>
          ) : (
            <>{t("videocall.audio_warning.remote_message")}</>
          )}
        </Message>
      </Content>
      <CloseButton onClick={onClose}>×</CloseButton>
    </BannerContainer>
  );
};

const BannerContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 152, 0, 0.95);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 100;
  max-width: 500px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(8px);
`;

const Icon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const Message = styled.div`
  font-size: 12px;
  line-height: 1.4;
`;

const Instructions = styled.div`
  font-size: 11px;
  margin-top: 6px;
  padding: 6px 10px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  line-height: 1.5;
`;

const InstructionStep = styled.div`
  margin: 2px 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 20px;
  cursor: pointer;
  padding: 0;
  margin-left: auto;
  flex-shrink: 0;
  opacity: 0.8;
  transition: opacity 0.2s;

  &:hover {
    opacity: 1;
  }
`;
